import { stat } from 'fs';
import Icon from '@atoms/Icon/Icon';
import { IMessage } from '@models/IMessage';
import { IProductListingStatusLabels } from '@models/IProductListingStatusLabels';
import InfoTypeEnum from '@models/enums/InfoTypeEnum';
import ProductStockStatusEnum from '@models/enums/ProductStockStatusEnum';
import InfoModal from '@molecules/InfoModal/InfoModal';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { useState } from 'react';

interface Props {
  readonly deliveryLabel: string;
  readonly backOrderText: IMessage;
  readonly preOrderText: IMessage;
  readonly status: ProductStockStatusEnum;
  readonly statusLabels: IProductListingStatusLabels;
  readonly className?: string;
  readonly dataTestId?: string;
  readonly onAddProductToCart: () => Promise<void>;
}

type StockStatusDetails = {
  readonly textName: string;
  readonly textColor: string;
  readonly bulletColor: string;
  readonly extraInfo?: IMessage;
};

const ProductDetailsStockStatus = ({
  backOrderText,
  preOrderText,
  status,
  statusLabels,
  className,
  dataTestId = 'Product-Details-Stock-Status',
  onAddProductToCart,
}: Props) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState<IMessage>({
    title: '',
    text: '',
  });
  const { moreInformationAriaLabel } = useAriaLabels();

  const openInfoModal = (content: IMessage) => {
    setInfoModalContent(content);
    setIsInfoModalOpen(true);
  };

  const handleInfoModalAddToCartClick = async () => {
    await onAddProductToCart();
    setIsInfoModalOpen(false);
  };

  const stockDisplayConfig: Partial<
    Record<ProductStockStatusEnum, StockStatusDetails>
  > = {
    [ProductStockStatusEnum.InStock]: {
      textName: statusLabels.inStockLabel,
      textColor: 'text-black-100',
      bulletColor: 'bg-green-100',
    },
    [ProductStockStatusEnum.OutOfStock]: {
      textName: statusLabels.outOfStockLabel,
      textColor: 'text-red-100',
      bulletColor: 'bg-red-100',
    },
    [ProductStockStatusEnum.SoldOut]: {
      textName: statusLabels.soldOutLabel,
      textColor: 'text-red-100',
      bulletColor: 'bg-red-100',
    },
    [ProductStockStatusEnum.ComingSoon]: {
      textName: statusLabels.comingSoonLabel,
      textColor: 'text-black-100',
      bulletColor: 'bg-yellow',
    },
    [ProductStockStatusEnum.PreOrder]: {
      textName: preOrderText.title,
      textColor: 'text-black-100',
      bulletColor: 'bg-yellow',
      extraInfo: preOrderText,
    },
    [ProductStockStatusEnum.BackOrder]: {
      textName: backOrderText.title,
      textColor: 'text-black-100',
      bulletColor: 'bg-yellow',
      extraInfo: backOrderText,
    },
  };
  const stockStatusDetails = stockDisplayConfig[status];
  if (!stockStatusDetails) return null;

  const { bulletColor, textColor, textName, extraInfo } = stockStatusDetails;

  return (
    <div
      className={classNames('flex items-center gap-x-1.5', className)}
      data-testid={dataTestId}
    >
      <div className={classNames('h-1.5 w-1.5 rounded-full', bulletColor)} />

      <p
        className={classNames('body-three body-three--bold', textColor)}
        data-testid={`${dataTestId}-Name`}
      >
        {textName}
      </p>

      {extraInfo && (
        <>
          <button
            type="button"
            className="mb-0.5 flex"
            onClick={() => {
              openInfoModal(extraInfo);
            }}
            aria-label={moreInformationAriaLabel}
            data-testid={`${dataTestId}-Info-Button`}
          >
            <Icon name="info" colorClassName="text-grey-400" size="1.25rem" />
          </button>

          <InfoModal
            type={InfoTypeEnum.Information}
            isModalOpen={isInfoModalOpen}
            toggleModal={setIsInfoModalOpen}
            title={infoModalContent.title}
            description={infoModalContent.text}
            buttonText={statusLabels.addToCartCTA}
            handleCTAClick={handleInfoModalAddToCartClick}
            dataTestId={`${dataTestId}-Info-Modal`}
          />
        </>
      )}
    </div>
  );
};

export default ProductDetailsStockStatus;
